import { authenticatedFetch } from "@lumi/data";
import { useCallback, useMemo, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { createRefreshCallback } from "../../auth/refreshCallback";
import { ContentEditor } from "../content-editing/ContentEditor";
import { createTitleAdapter } from "../content-editing/adapters/titleAdapter";
import { useTitle, useTitleMutations } from "../content-editing/hooks/useTitleMutations";

/**
 * Edit Published Title Page - Thin Wrapper
 *
 * This component wraps ContentEditor with title-specific logic using the adapter pattern.
 * All form logic, auto-save, and UI are handled by ContentEditor.
 */
export function EditTitle(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, refreshAuth } = useAuth();

  // Fetch title data
  const { data: title, loading, error, refetch } = useTitle(id || null);

  // Get mutation functions
  const { updateTitle } = useTitleMutations(id || null);

  // Encoding trigger function
  const triggerEncoding = useCallback(
    async (titleId: string) => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
      await authenticatedFetch(
        `${baseUrl}/v1/admin/titles/${titleId}/trigger-encoding`,
        { method: "POST" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
    },
    [accessToken, refreshAuth],
  );

  // Create adapter that connects ContentEditor to title logic
  // Memoize to prevent infinite re-render loop
  // Note: refetch is intentionally NOT in dependencies to prevent infinite loops
  const adapter = useMemo(
    () => createTitleAdapter(title, loading, error, () => Promise.resolve(refetch()), updateTitle, triggerEncoding),
    [title, loading, error, updateTitle, triggerEncoding],
  );

  // Handle navigation back to titles library
  const handleCancel = () => {
    navigate("/admin/titles");
  };

  // Validate ID
  if (!id) {
    return (
      <div className="mx-auto py-12 text-center">
        <p className="text-red-400">No title ID provided</p>
      </div>
    );
  }

  // Render ContentEditor with title adapter
  return (
    <ContentEditor
      id={id}
      adapter={adapter}
      onCancel={handleCancel}
      breadcrumbPath={[
        { to: "/admin/titles", label: "Title Library" },
        { to: "", label: "Edit Title" },
      ]}
      titleData={title}
      onRefreshTitle={() => Promise.resolve(refetch())}
    />
  );
}
